// src/store/notificationStore.ts
import { create } from 'zustand';
import { Notification } from '../types';
import { notificationService } from '../services/notification';

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isDropdownOpen: boolean;
  isLoading: boolean;
  setNotifications: (notifications: Notification[]) => void;
  setUnreadCount: (count: number) => void;
  toggleDropdown: () => void;
  closeDropdown: () => void;
  fetchNotifications: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isDropdownOpen: false,
  isLoading: false,
  setNotifications: (notifications) => set({ notifications }),
  setUnreadCount: (count) => set({ unreadCount: count }),
  toggleDropdown: () => set((state) => ({ isDropdownOpen: !state.isDropdownOpen })),
  closeDropdown: () => set({ isDropdownOpen: false }),

  fetchNotifications: async () => {
    set({ isLoading: true });
    try {
      const notifications = await notificationService.getAll();
      const data = Array.isArray(notifications)
        ? notifications
        : (Array.isArray(notifications?.data?.data)
          ? notifications.data.data
          : (Array.isArray(notifications?.data) ? notifications.data : []));
      const unread = data.filter((n: Notification) => !n.read_at).length;
      set({ notifications: data, unreadCount: unread, isLoading: false });
    } catch (error) {
      console.error('Failed to fetch notifications', error);
      set({ isLoading: false });
    }
  },

  markAsRead: async (id) => {
    // Optimistic update
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === id ? { ...n, read_at: new Date().toISOString() } : n
      ),
      unreadCount: Math.max(0, state.unreadCount - 1),
    }));
    try {
      await notificationService.markAsRead(id);
    } catch (error) {
      // Revert if failed? For now, just log.
      console.error('Failed to mark notification as read', error);
    }
  },

  markAllAsRead: async () => {
    // Optimistic update
    set((state) => ({
      notifications: state.notifications.map((n) => ({
        ...n,
        read_at: n.read_at || new Date().toISOString(),
      })),
      unreadCount: 0,
    }));
    try {
      await notificationService.markAllAsRead();
    } catch (error) {
      console.error('Failed to mark all notifications as read', error);
    }
  },

  deleteNotification: async (id) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    }));
    try {
      await notificationService.delete(id);
    } catch (error) {
      console.error('Failed to delete notification', error);
    }
  }
}));
